import React, {useCallback, useEffect, useState} from 'react';
import {View, FlatList, StyleSheet, TouchableOpacity} from 'react-native';
import {Text, Checkbox, Button, Divider} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useAddOnStore} from '../../store/useAddOnStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const AddOnSelectorScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {addOns, fetchAddOns} = useAddOnStore();

  const preSelected = route.params?.selectedAddOns || [];
  const [selected, setSelected] = useState(() => {
    const map = {};
    preSelected.forEach(a => { map[a.id] = a; });
    return map;
  });

  useEffect(() => {
    fetchAddOns();
  }, [fetchAddOns]);

  const toggleAddOn = useCallback((addOn) => {
    setSelected(prev => {
      const next = {...prev};
      if (next[addOn.id]) {
        delete next[addOn.id];
      } else {
        next[addOn.id] = {id: addOn.id, name: addOn.name, price: addOn.price};
      }
      return next;
    });
  }, []);

  const selectedList = Object.values(selected);
  const total = selectedList.reduce((sum, a) => sum + (parseFloat(a.price) || 0), 0);

  const handleDone = useCallback(() => {
    navigation.navigate({
      name: 'OrderLineDetail',
      params: {selectedAddOns: selectedList},
      merge: true,
      pop: true,
    });
  }, [navigation, selectedList]);

  const renderItem = useCallback(({item}) => (
    <TouchableOpacity style={styles.row} onPress={() => toggleAddOn(item)} activeOpacity={0.7}>
      <Checkbox
        status={selected[item.id] ? 'checked' : 'unchecked'}
        onPress={() => toggleAddOn(item)}
        color={colors.primary}
      />
      <View style={styles.info}>
        <Text variant="bodyMedium" style={styles.name}>{item.name}</Text>
        <Text variant="bodySmall" style={styles.price}>{'\u20B9'}{item.price}</Text>
      </View>
    </TouchableOpacity>
  ), [selected, toggleAddOn]);

  return (
    <View style={styles.container}>
      <GradientHeader title="Add-ons" onBack={() => navigation.goBack()} />

      <FlatList
        data={addOns}
        keyExtractor={item => String(item.id)}
        renderItem={renderItem}
        extraData={selected}
        ItemSeparatorComponent={Divider}
        contentContainerStyle={styles.listContent}
      />

      <View style={styles.bottomBar}>
        <Text variant="titleSmall" style={styles.totalText}>
          Total: {'\u20B9'}{total} ({selectedList.length} selected)
        </Text>
        <Button mode="contained" onPress={handleDone} style={styles.doneButton} buttonColor={colors.primary}>
          Done
        </Button>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  listContent: {paddingBottom: spacing.xxl},
  row: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: spacing.sm, paddingVertical: spacing.sm,
    backgroundColor: colors.surface,
  },
  info: {flex: 1, marginLeft: spacing.sm},
  name: {color: colors.text},
  price: {color: colors.textSecondary},
  bottomBar: {
    flexDirection: 'row', padding: spacing.md, backgroundColor: colors.surface,
    alignItems: 'center', justifyContent: 'space-between', elevation: 4,
  },
  totalText: {color: colors.text, fontWeight: '600'},
  doneButton: {borderRadius: 8, minWidth: 100},
});

export default AddOnSelectorScreen;
